import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { formatSpecNumber } from '../../domain/cars/carProperties';
import { reactionSeconds, speedShare } from '../../domain/game/duelScoring';
import useCountUp from '../../application/motion/useCountUp';
import { SoundOffIcon, SoundOnIcon } from '../icons/ControlIcons';

/**
 * How the run is going: the score, the streak and the record.
 *
 * THE SCORE IS THE FIGURE, drawn the way a car's headline spec is drawn —
 * tabular, large, the number first — because it is the same kind of object. The
 * whole product's typographic argument is that a number can be the point, and
 * this is the one number on any screen that belongs to the reader.
 *
 * AND IT ROLLS (SPEC-007 D-62). A round that paid two hundred points used to
 * move it in a single frame, which means the reader saw a different number
 * rather than a number changing — and the size of the payment was something to
 * work out by remembering the last one. `useCountUp` is the browser half of
 * that; this component only asks for it. The pop is kept and keyed on the real
 * score rather than on the rolling one, so it fires ONCE per round while the
 * digits climb, instead of on every frame of the climb.
 *
 * The score leads and the streak sits beside it, which is the right way round
 * now that a round pays for speed: two readers can reach the same nine by very
 * different routes, and the score is what tells them apart. The streak is still
 * the RULE — one wrong answer ends the run — so it stays on screen, quietly.
 *
 * The ROUND is deliberately not here. While a question is open it is always the
 * streak plus one, and a second drawing of one number is a thing to keep in step
 * for no reader's benefit.
 *
 * THE CLOCK AND THE LADDER USED TO BE IN HERE and are now `DuelClock` and
 * `DuelLadder`, drawn at the other two ends of the column (SPEC-007 D-58): the
 * clock under the cards, because that is where the eyes are in the seconds it
 * matters, and the ladder above everything, because it is the only thing on the
 * screen that is not about this round. What is left is one row.
 *
 * The score's live region is the SCORE ALONE — wrapping the clock in one would
 * have had a screen reader announce a number every second for the length of a
 * run, which was the whole screen made unusable by a detail nobody needed
 * spoken. It is worth stating even though the clock has left, because the reason
 * it left is not the reason the region is narrow.
 */
export default function DuelScoreboard({ run, sound = null }) {
    const { languageCode } = useLocale();
    const t = useTranslate();
    const score = useCountUp(run.score);
    /**
     * Drawn only on a round that was both RIGHT and TIMED (D-74).
     *
     * `revealed` is the status a correct answer leaves the run in; `over` is an
     * ending, and a wrong answer's reaction is a real number and an unkind one —
     * the round paid nothing, and telling somebody how quickly they were wrong is
     * the screen being clever at them. A round the clock ran out on has no
     * reaction at all, which is not a slow answer but the absence of one.
     */
    const isTimed = run.status === 'revealed' && Number.isFinite(run.reaction);

    return (
        <div className="duel-score">
            <p className="duel-score__streak">
                {/* Keyed on the RUN's score rather than on the rolling one, so
                    the pop fires once per round while the digits climb rather
                    than once per frame of the climb — a CSS animation only runs
                    for an element that is new.

                    ARIA-HIDDEN, and the live region below is why. A rolling
                    number inside a `role="status"` is twenty-five announcements
                    per round: the same defect the clock's own comment has warned
                    about since the board landed, arrived at from the other
                    side. */}
                <span
                    className="duel-score__figure figure"
                    key={run.score}
                    aria-hidden="true"
                >
                    {score}
                </span>
                <span className="duel-score__label" aria-hidden="true">{t('duel.score')}</span>

                {/* WHAT BOUGHT THE LAST ROUND, against the counter it bought it
                    for (D-74). A round is paid on the real interval between the
                    question and the tap, and a game that pays for something it
                    never shows is asking the reader to take its word for it: two
                    rounds both said "correct", both rolled this figure up, and
                    the quicker one was worth twice as much for reasons nothing
                    on screen mentioned.

                    IT IS A LAP TIME, which is why it is a figure in the numeric
                    face rather than a sentence: this is a product about cars,
                    and "0.84s" is a thing you try to beat in a way that "very
                    fast" is not. It warms towards speed yellow as the round gets
                    quicker — the same two-token mix the ladder's caption uses
                    (D-65) — so a reader who never reads the number still sees
                    which of their rounds were the fast ones.

                    Keyed on the ROUND so it arrives with each answer rather than
                    sitting there, and `aria-hidden` like every other drawing on
                    this plate: the region below carries the score itself, and a
                    time announced every round is a number nobody asked for. */}
                {isTimed ? (
                    <span
                        className="duel-score__reaction figure"
                        key={run.round}
                        style={{ '--speed-share': speedShare(run.points) }}
                        aria-hidden="true"
                    >
                        {t('duel.reaction', {
                            seconds: formatSpecNumber(reactionSeconds(run.reaction), {
                                languageCode,
                                fractionDigits: 2,
                                // A reaction is never long enough to group, and a
                                // separator in one would read as a second figure.
                                grouped: false,
                            }),
                        })}
                    </span>
                ) : null}

                {/* What the score actually IS, said once when it moves. It reads
                    the run rather than the roll, so the announcement is the
                    number the reader ended on and not whichever frame the
                    screen reader happened to catch. */}
                <span className="visually-hidden" role="status">
                    {t('duel.finalScore', { count: run.score })}
                </span>
            </p>

            <p className="duel-score__meta">
                <span className="duel-score__run">
                    {t('duel.streak')}{' '}
                    {/* Keyed on the count so the tick replays each time the
                        streak moves. The score beside it has popped since the
                        board landed; the streak is the RULE — one wrong answer
                        ends the run — and a rule that never moves on screen
                        stops being felt by about the fourth round. */}
                    <span className="duel-score__count" key={run.streak}>{run.streak}</span>
                </span>
                {run.best > 0 ? (
                    <span className="duel-score__best">{t('duel.best', { count: run.best })}</span>
                ) : null}
            </p>

            {/* The only control on this screen that is not a card, and the only
                way to stop a sound the app makes on its own. It is here rather
                than in a settings sheet the product does not have, and it costs
                no height: the score figure beside it is already taller than the
                tap target. */}
            {sound ? (
                <button
                    type="button"
                    className="duel-score__sound"
                    onClick={sound.toggle}
                    aria-pressed={sound.isOn}
                    aria-label={t(sound.isOn ? 'duel.sound.on' : 'duel.sound.off')}
                >
                    {sound.isOn ? <SoundOnIcon size={20} /> : <SoundOffIcon size={20} />}
                </button>
            ) : null}
        </div>
    );
}
